import { Api } from '../../utils/api';
import { HOME_BUNDLE_URLS } from '../../utils/api/urls';
// Action creators from the slice the bundle feeds. Imported rather than
// dispatched through a reducer of its own: the bundle owns no state, it only
// fills the state the widgets already read, so none of them had to change how
// they consume it.
import {
  getCourseListSuccess,
  getStudentWallListSuccess,
  getTeamListSuccess,
  getGoogleReviewsSuccess,
  getGoogleReviews,
  // The stored Google aggregate, when the backend has one to serve.
  getGoogleReviewsMeta,
  // Used to fall back to the standalone endpoints if the bundle request itself
  // fails, so a bundle outage degrades to the old behaviour instead of a blank
  // section.
  getAllCourses,
  getStudentwalls,
  getTeams,
} from './course';

// ----------------------------------------------------------------------
// Homepage bundle.
//
// The homepage used to issue one request per widget. `getHomeBundle` on the
// backend returns the heavy, unbounded sections — courses, achievers, teams,
// and google_reviews — in one response, each already sliced to the cap the
// widget applies to itself. Those are the only four bundled: none of the
// other homepage sections is capped client-side, so a server-side cap would
// silently drop rows the page currently shows.
//
// Two rules keep this safe:
//
// 1. **Fill empty slots only.** A section is written only when the store holds
//    nothing for it. A visitor who opened /courses (which fetches the full
//    course list) or /team and then returned must not have that fuller list
//    replaced by the bundle's six- and four-row slices.
//
// 2. **Never dispatch an empty array as a success.** An empty section is left
//    alone, so the widget's own fallback still engages.
//
// The bundle also carries `review_meta`, the stored Google rating and review
// count. That one is not a row section: it is a single object, omitted entirely
// when nothing is stored, and it fills the same `googleReviewMeta` slot a live
// Places lookup uses. Fill-empty-only applies to it as well.
//
// Callers must also suppress the per-widget fetches, or both run. The widgets
// take a `skipFetch` prop for that, set on the homepage only.
// ----------------------------------------------------------------------

// Bundle key -> the path to the state it fills, the action that fills it, and
// the standalone thunk to fall back to if the bundle request fails.
const SECTIONS = [
  { key: 'courses', path: ['courses'], action: getCourseListSuccess, refetch: getAllCourses },
  { key: 'achievers', path: ['studentwalls'], action: getStudentWallListSuccess, refetch: getStudentwalls },
  { key: 'teams', path: ['teams'], action: getTeamListSuccess, refetch: getTeams },
  { key: 'google_reviews', path: ['googleReviews'], action: getGoogleReviewsSuccess, refetch: getGoogleReviews },
];

function isPopulated(courseState, path) {
  const value = path.reduce((node, key) => (node == null ? undefined : node[key]), courseState);
  return Array.isArray(value) && value.length > 0;
}

// ----------------------------------------------------------------------

export function getHomeBundle() {
  return async (dispatch, getState) => {
    // The stored aggregate is filled unconditionally, unlike the row sections: it
    // is a single small object rather than a list, so there is no "a fuller list
    // would be replaced" failure mode to protect against. It is still fill-empty-
    // only, so a live Places lookup that already supplied fresher figures wins.
    const metaPending = !getState().course.googleReviewMeta;

    const pending = SECTIONS.filter((section) => !isPopulated(getState().course, section.path));

    // Everything is already in the store — a repeat visit to the homepage, or a
    // page that already loaded some of this itself. Nothing left to fill.
    if (!pending.length && !metaPending) {
      return;
    }

    try {
      const response = await Api.unget(HOME_BUNDLE_URLS.list);
      const payload = response.data ?? {};

      pending.forEach((section) => {
        const rows = payload[section.key];

        if (!Array.isArray(rows) || rows.length === 0) {
          return;
        }

        // Re-read state: a sibling section may have been filled between the
        // snapshot above and this dispatch.
        if (!isPopulated(getState().course, section.path)) {
          dispatch(section.action(rows));
        }
      });

      // The backend omits `review_meta` entirely when it has no stored figure
      // (ASAP-RVS-005). An absent key must stay absent in the store too: writing
      // `null` here would be indistinguishable from "fetched, nothing stored" to
      // the component, and the section renders no aggregate either way — but the
      // store should not claim to have data it does not have.
      if (metaPending && payload.review_meta && typeof payload.review_meta === 'object') {
        dispatch(getGoogleReviewsMeta(payload.review_meta));
      }
    } catch (error) {
      // A dead bundle must not take the homepage with it. `pending` is only
      // non-empty when the store was empty, so re-running the three widget
      // thunks reproduces exactly the old behaviour.
      console.error(error);

      pending.forEach((section) => dispatch(section.refetch()));
    }
  };
}

export default { getHomeBundle };